import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Body1,
  Button,
  Card,
  Checkbox,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  CalendarRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  format,
} from 'date-fns';
import { Link } from 'react-router-dom';
import type { TimeSlot } from '../types/booking.types';
import type { StoredReport } from '@/features/discovery/types/discovery.types';
import { transformrBrand } from '@/theme/transformrTheme';

interface CalendarPickerProps {
  slots: TimeSlot[];
  timezone: string;
  selectedSlot: TimeSlot | null;
  onSelectSlot: (slot: TimeSlot) => void;
  isLoading: boolean;
  reports: StoredReport[];
  selectedReportIds: string[];
  onReportSelectionChange: (ids: string[]) => void;
  isLoadingReports: boolean;
}

const MAX_REPORTS = 5;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXL,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  calendarSection: {
    flex: '40 1 0',
    minWidth: '0',
    '@media (max-width: 768px)': {
      flex: '1 1 auto',
    },
  },
  calendarCard: {
    padding: tokens.spacingHorizontalL,
    height: '420px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
  },
  monthTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: tokens.spacingVerticalXS,
  },
  weekdayLabel: {
    textAlign: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    padding: '4px 0',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gridAutoRows: '1fr',
    gap: tokens.spacingHorizontalXXS,
    flex: 1,
  },
  dayCell: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '36px',
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'default',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForegroundDisabled,
    padding: '0',
    outline: 'none',
  },
  dayCellClickable: {
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  dayCellOutside: {
    color: tokens.colorNeutralForeground4,
    backgroundColor: 'transparent',
  },
  dayCellToday: {
    fontWeight: 700,
  },
  dayCellSelected: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: 600,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  dot: {
    position: 'absolute',
    bottom: '3px',
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
  },
  dotSelected: {
    backgroundColor: tokens.colorNeutralForegroundOnBrand,
  },
  slotsSection: {
    flex: '25 1 0',
    minWidth: '0',
    '@media (max-width: 768px)': {
      flex: '1 1 auto',
    },
  },
  reportsSection: {
    flex: '35 1 0',
    minWidth: '0',
    '@media (max-width: 768px)': {
      flex: '1 1 auto',
    },
  },
  reportsCard: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
    height: '420px',
    boxSizing: 'border-box',
  },
  reportsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  reportsTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  reportsCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  reportsSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
  reportsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  reportRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  reportMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  infoIcon: {
    cursor: 'help',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: tokens.spacingHorizontalXS,
  },
  slotsCard: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalMNudge,
    height: '420px',
    boxSizing: 'border-box',
  },
  slotsDayHeader: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  slotsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  slotRow: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: '0',
    borderLeft: `3px solid ${tokens.colorNeutralStroke2}`,
    borderTop: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    borderTopLeftRadius: tokens.borderRadiusSmall,
    borderBottomLeftRadius: tokens.borderRadiusSmall,
    padding: '8px 10px',
    backgroundColor: 'transparent',
    // Match the Prompt-library questions: green-soft hover + selection.
    ':hover': {
      backgroundColor: transformrBrand.greenSoft,
      borderLeftColor: transformrBrand.greenAlt,
    },
  },
  slotRowSelected: {
    backgroundColor: transformrBrand.greenSoft,
    borderLeftColor: transformrBrand.greenAlt,
    color: transformrBrand.greenInk,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: transformrBrand.greenSoft,
      borderLeftColor: transformrBrand.greenAlt,
    },
  },
  slotTime: {
    flex: '0 0 35%',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
  slotDuration: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  slotDurationSelected: {
    color: tokens.colorNeutralForegroundOnBrand,
    opacity: 0.85,
  },
  selectPrompt: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: '24px 0',
  },
  emptyState: {
    padding: tokens.spacingHorizontalXXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 0',
  },
});

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Build a timezone-safe date key from an ISO string.
 * Uses Intl.DateTimeFormat so the date boundary respects the booking timezone.
 */
function toDateKey(iso: string, timezone: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  const day = parts.find((p) => p.type === 'day')!.value;
  return `${y}-${m}-${day}`;
}

/**
 * Build a date key for a local Date object (calendar grid dates).
 */
function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function CalendarPicker({
  slots,
  timezone,
  selectedSlot,
  onSelectSlot,
  isLoading,
  reports,
  selectedReportIds,
  onReportSelectionChange,
  isLoadingReports,
}: CalendarPickerProps) {
  const styles = useStyles();
  const tz = timezone || 'Australia/Sydney';

  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Group slots by date key (timezone-aware)
  const slotsByDate = useMemo(() => {
    const map = new Map<string, TimeSlot[]>();
    for (const slot of slots) {
      const key = toDateKey(slot.start, tz);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(slot);
    }
    return map;
  }, [slots, tz]);

  // Auto-select first available date on load
  useEffect(() => {
    if (slots.length === 0 || selectedDate) return;
    const firstSlot = slots[0];
    const key = toDateKey(firstSlot.start, tz);
    // Parse the key back to a local date for the calendar
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    setSelectedDate(date);
    setViewMonth(startOfMonth(date));
  }, [slots, tz, selectedDate]);

  // Formatters
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: tz,
      }),
    [tz]
  );

  const dayHeaderFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: tz,
      }),
    [tz]
  );

  const reportDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    []
  );

  const handleReportToggle = useCallback(
    (analysisId: string) => {
      if (selectedReportIds.includes(analysisId)) {
        onReportSelectionChange(
          selectedReportIds.filter((id) => id !== analysisId)
        );
      } else if (selectedReportIds.length < MAX_REPORTS) {
        onReportSelectionChange([...selectedReportIds, analysisId]);
      }
    },
    [selectedReportIds, onReportSelectionChange]
  );

  // Calendar grid days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const handlePrevMonth = useCallback(
    () => setViewMonth((m) => subMonths(m, 1)),
    []
  );
  const handleNextMonth = useCallback(
    () => setViewMonth((m) => addMonths(m, 1)),
    []
  );

  const handleDateClick = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  // Slots for the selected date
  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    const key = localDateKey(selectedDate);
    return slotsByDate.get(key) || [];
  }, [selectedDate, slotsByDate]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading available times..." />
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <Card className={styles.emptyState}>
        <CalendarRegular
          fontSize={40}
          color={tokens.colorNeutralForeground3}
        />
        <Body1>
          No available slots found for the next few days. Please try again later
          or contact us directly.
        </Body1>
      </Card>
    );
  }

  return (
    <div className={styles.container}>
      {/* Calendar Grid */}
      <div className={styles.calendarSection}>
        <Card className={styles.calendarCard}>
          {/* Month header */}
          <div className={styles.calendarHeader}>
            <Button
              appearance="subtle"
              icon={<ChevronLeftRegular />}
              onClick={handlePrevMonth}
              aria-label="Previous month"
            />
            <Text className={styles.monthTitle}>
              {format(viewMonth, 'MMMM yyyy')}
            </Text>
            <Button
              appearance="subtle"
              icon={<ChevronRightRegular />}
              onClick={handleNextMonth}
              aria-label="Next month"
            />
          </div>

          {/* Weekday labels */}
          <div className={styles.weekdayRow}>
            {WEEKDAYS.map((wd) => (
              <div key={wd} className={styles.weekdayLabel}>
                {wd}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className={styles.daysGrid}>
            {calendarDays.map((day) => {
              const key = localDateKey(day);
              const inMonth = isSameMonth(day, viewMonth);
              const today = isToday(day);
              const hasSlots = slotsByDate.has(key);
              const isSelected =
                selectedDate !== null && isSameDay(day, selectedDate);

              const cellClasses = mergeClasses(
                styles.dayCell,
                !inMonth && styles.dayCellOutside,
                today && styles.dayCellToday,
                isSelected && styles.dayCellSelected,
                hasSlots && styles.dayCellClickable
              );

              return (
                <button
                  key={key}
                  className={cellClasses}
                  onClick={hasSlots ? () => handleDateClick(day) : undefined}
                  tabIndex={hasSlots ? 0 : -1}
                  aria-label={`${format(day, 'd MMMM yyyy')}${hasSlots ? ', has available slots' : ''}`}
                  aria-selected={isSelected}
                  type="button"
                >
                  {day.getDate()}
                  {hasSlots && (
                    <span
                      className={mergeClasses(
                        styles.dot,
                        isSelected && styles.dotSelected
                      )}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Time Slots Panel */}
      <div className={styles.slotsSection}>
        <Card className={styles.slotsCard}>
          {selectedDate ? (
            <>
              <Text className={styles.slotsDayHeader}>
                {dayHeaderFormatter.format(selectedDate)}
              </Text>
              {selectedDateSlots.length > 0 ? (
                <>
                  <Text className={styles.selectPrompt}>
                    Select a time
                  </Text>
                  <div className={styles.slotsList}>
                    {selectedDateSlots.map((slot) => {
                      const isSlotSelected =
                        selectedSlot?.start === slot.start &&
                        selectedSlot?.staffUserId === slot.staffUserId;
                      return (
                        <button
                          key={`${slot.start}-${slot.staffUserId}`}
                          type="button"
                          className={mergeClasses(
                            styles.slotRow,
                            isSlotSelected && styles.slotRowSelected
                          )}
                          onClick={() => onSelectSlot(slot)}
                        >
                          <span className={styles.slotTime}>
                            {timeFormatter.format(new Date(slot.start))}
                          </span>
                          <span
                            className={mergeClasses(
                              styles.slotDuration,
                              isSlotSelected && styles.slotDurationSelected
                            )}
                          >
                            30 minutes
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <Body1 className={styles.emptyHint}>
                  No times available on this date.
                </Body1>
              )}
            </>
          ) : (
            <Body1 className={styles.emptyHint}>
              Select a date to see available times
            </Body1>
          )}
        </Card>
      </div>

      {/* Reports Panel */}
      <div className={styles.reportsSection}>
        <Card className={styles.reportsCard}>
          <div className={styles.reportsHeader}>
            <Text className={styles.reportsTitle}>Select reports</Text>
            <Text className={styles.reportsCount}>
              {selectedReportIds.length} of {MAX_REPORTS}
            </Text>
          </div>
          <Text className={styles.reportsSubtitle}>
            Attach 1–5 reports to discuss
          </Text>

          {isLoadingReports ? (
            <Spinner size="small" label="Loading reports..." />
          ) : reports.length === 0 ? (
            <MessageBar intent="warning">
              <MessageBarBody>
                You must generate at least one report before booking a meeting.{' '}
                <Link to="/">Go to Home</Link>
              </MessageBarBody>
            </MessageBar>
          ) : (
            <div className={styles.reportsList}>
              {reports.map((report) => {
                const isSelected = selectedReportIds.includes(
                  report.analysisId
                );
                const isDisabled =
                  !isSelected && selectedReportIds.length >= MAX_REPORTS;

                return (
                  <div key={report.analysisId} className={styles.reportRow}>
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleReportToggle(report.analysisId)}
                      label={
                        <span>
                          {report.fileName}{' '}
                          <Text className={styles.reportMeta}>
                            {report.storedAt
                              ? reportDateFormatter.format(
                                  new Date(report.storedAt)
                                )
                              : ''}
                          </Text>
                          {report.aiGoals && (
                            <Tooltip
                              content={report.aiGoals}
                              relationship="description"
                            >
                              <span className={styles.infoIcon}>
                                <InfoRegular
                                  fontSize={14}
                                  color={tokens.colorNeutralForeground3}
                                />
                              </span>
                            </Tooltip>
                          )}
                        </span>
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
